import { useAuth } from "@clerk/react"
import { createORPCClient } from "@orpc/client"
import { RPCLink } from "@orpc/client/websocket"
import ReconnectingWebSocket from "partysocket/ws"
import { useEffect, useRef, useState } from "react"
import { type GameAdminOrpcClient, getGameAdminWsUrl } from "./client"

/**
 * Wraps an oRPC client proxy to prevent React from mistaking it for a Promise.
 */
function wrapClientProxy<T extends object>(client: T): T {
	return new Proxy(client, {
		get(target, prop, receiver) {
			if (prop === "then") {
				return undefined
			}
			const value = Reflect.get(target, prop, receiver)
			if (value && typeof value === "object") {
				return wrapClientProxy(value)
			}
			return value
		},
	})
}

/**
 * Hook to create a game-admin oRPC client over WebSocket.
 * Use this for subscriptions (event iterators) which benefit from persistent
 * WebSocket connections with automatic reconnection.
 *
 * The WebSocket is created inside a useEffect so that React 18 Strict Mode's
 * unmount/remount cycle correctly tears down the old socket and creates a new
 * one, rather than leaving a closed socket behind.
 */
export function useOrpcWs() {
	const { getToken: clerkGetToken, isLoaded, isSignedIn } = useAuth()
	const isAuthReady = isLoaded && isSignedIn

	// Keep getToken in a ref so the effect doesn't depend on it.
	const getTokenRef = useRef(clerkGetToken)
	getTokenRef.current = clerkGetToken

	const [client, setClient] = useState<GameAdminOrpcClient | null>(null)

	useEffect(() => {
		const wsUrl = getGameAdminWsUrl()

		const websocket = new ReconnectingWebSocket(
			async () => {
				const token = await getTokenRef.current()
				const url = new URL(wsUrl)
				if (token) {
					url.searchParams.set("token", token)
				}
				return url.toString()
			},
			[],
			{
				maxRetries: Number.POSITIVE_INFINITY,
				connectionTimeout: 10_000,
				maxReconnectionDelay: 30_000,
			},
		)

		const link = new RPCLink({
			websocket: websocket as unknown as WebSocket,
		})

		const orpcClient = createORPCClient(link)
		// Wrap in arrow function to prevent React from treating the proxy
		// as a functional updater (oRPC client is a Proxy of a function)
		setClient(() => wrapClientProxy(orpcClient) as GameAdminOrpcClient)

		return () => {
			websocket.close()
		}
	}, [])

	return { client, isAuthReady }
}
